import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import type { ApprovalRequest } from '@/approvals/types'
import type { BoardCard, BoardColumn } from '@/card/dealApi'
import { BlockState, QueryErrorState } from '@/components/BlockState'
import { Icon } from '@/components/Icon'
import { StagePips } from '@/components/StagePips'
import { ApiError } from '@/lib/api'
import { formatMoney, formatTotals } from '@/lib/format'
import type { Locale } from '@/i18n'
import {
  approveRequest,
  fetchApprovalQueue,
  fetchBoardForDashboard,
  fetchFunnelConversion,
  fetchFunnels,
  fetchReceivables,
  rejectRequest,
} from './leaderApi'
import { iso } from './types'
import type { AgeingLine, StageConversion } from './types'
import '@/styles/myday.css'

/**
 * Дашборд руководителя (макет Dashboard.dc.html) поверх существующих API.
 *
 * Сверху — то, где нужно решение: согласования с кнопками прямо в строке. Ниже — картина:
 * план/факт, воронка, дебиторка по срокам, застрявшие сделки. Ни одной цифры своей: каждая
 * берётся из того же ответа, что показывает соответствующий раздел, и в той же области
 * видимости смотрящего — менеджер, открывший дашборд, увидит свою воронку, а не компанию.
 *
 * ## Каждый блок отвечает за себя
 *
 * Права у блоков разные (`system.approval`, `sales.deal`, `billing.invoice`), и отказ по
 * одному не роняет остальные: блок без права говорит «нет доступа» словами, а не пустотой.
 *
 * ## Что честно не сделано
 *
 * - **План/факт по компании**: источника нет — цели KPI заводятся на сотрудников и
 *   отделы, сводного плана компании в системе нет. Блок говорит «не настроено», а не
 *   рисует шкалу из выдуманного знаменателя.
 * - **Ответственный у застрявших и у просрочек**: на карточке доски есть только его
 *   идентификатор, в строке дебиторки — ни того. Колонка не рисуется, чтобы не показывать
 *   идентификаторы вместо имён.
 */
export function LeaderOverview() {
  const { t, user, locale } = useApp()
  const mayApprove = can(user, 'system.approval', 'read')
  const mayDeals = can(user, 'sales.deal', 'read')
  const mayInvoices = can(user, 'billing.invoice', 'read')

  const approvals = useQuery({
    queryKey: ['approval-queue'],
    enabled: mayApprove,
    queryFn: ({ signal }) => fetchApprovalQueue(signal),
    retry: false,
  })
  const funnels = useQuery({
    queryKey: ['funnels'],
    enabled: mayDeals,
    queryFn: ({ signal }) => fetchFunnels(signal),
    retry: false,
  })
  const funnel = funnels.data?.find((f) => f.defaultFunnel) ?? funnels.data?.[0]
  const board = useQuery({
    queryKey: ['dashboard-board', funnel?.id],
    enabled: Boolean(funnel),
    queryFn: ({ signal }) => fetchBoardForDashboard(funnel!.id, signal),
    retry: false,
  })
  const month = monthToDate(new Date())
  const conversion = useQuery({
    queryKey: ['dashboard-conversion', funnel?.id, month.from, month.to],
    enabled: Boolean(funnel),
    queryFn: ({ signal }) => fetchFunnelConversion(funnel!.id, month, signal),
    retry: false,
  })
  const receivables = useQuery({
    queryKey: ['dashboard-receivables'],
    enabled: mayInvoices,
    queryFn: ({ signal }) => fetchReceivables(signal),
    retry: false,
  })

  if (!mayApprove && !mayDeals && !mayInvoices) {
    return (
      <div className="state-box">
        <BlockState kind="forbidden" title={t('dash.forbidden.title')} description={t('dash.forbidden.text')} />
      </div>
    )
  }

  const updated = Math.max(approvals.dataUpdatedAt, board.dataUpdatedAt, receivables.dataUpdatedAt)

  return (
    <section className="stack" style={{ gap: 14 }} aria-label={t('dash.leader')}>
      <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
        <span className="h2">{t('dash.leader')}</span>
        {updated > 0 && (
          <span className="t-sm muted">
            {t('dash.fresh', {
              time: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(updated)),
            })}
          </span>
        )}
      </div>

      <ApprovalsBlock allowed={mayApprove} query={approvals} />

      <div className="md-dash-grid">
        <PlanFactBlock />
        <FunnelBlock
          allowed={mayDeals}
          isLoading={funnels.isLoading || board.isLoading}
          error={funnels.error ?? board.error}
          onRetry={() => void (funnels.error ? funnels.refetch() : board.refetch())}
          noFunnel={funnels.isSuccess && !funnel}
          columns={board.data?.columns}
          conversion={conversion.data}
        />
        <ReceivablesBlock
          allowed={mayInvoices}
          isLoading={receivables.isLoading}
          error={receivables.error}
          onRetry={() => void receivables.refetch()}
          lines={receivables.data}
        />
        <StuckBlock
          allowed={mayDeals}
          isLoading={funnels.isLoading || board.isLoading}
          error={funnels.error ?? board.error}
          onRetry={() => void board.refetch()}
          columns={board.data?.columns}
        />
      </div>
    </section>
  )
}

// ------------------------------------------------------------------ согласования

function ApprovalsBlock({
  allowed,
  query,
}: {
  allowed: boolean
  query: { isLoading: boolean; error: unknown; data?: { requests: ApprovalRequest[] }; refetch: () => unknown }
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const done = () => {
    setNotice(null)
    void queryClient.invalidateQueries({ queryKey: ['approval-queue'] })
  }
  const failed = (error: unknown) => setNotice(error instanceof ApiError ? error.message : t('app.error'))
  const approve = useMutation({ mutationFn: (id: string) => approveRequest(id, ''), onSuccess: done, onError: failed })
  const reject = useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) => rejectRequest(id, comment),
    onSuccess: done,
    onError: failed,
  })
  // «Ждут вашего решения» — только то, что этот человек может решить сейчас: остальное
  // ждёт другого согласующего, и кнопка на нём всегда отказывала бы.
  const waiting = (query.data?.requests ?? []).filter((request) => request.canDecide)
  const busy = approve.isPending || reject.isPending

  return (
    <section className="panel" aria-labelledby="dash-h-ap">
      <div className="panel-h">
        <span className="h2" id="dash-h-ap">{t('dash.approvals')}</span>
        {allowed && query.data && (
          <span className={waiting.length > 0 ? 'count-pill is-danger' : 'count-pill'}>{waiting.length}</span>
        )}
        <span className="spacer" />
        {allowed && <Link className="btn btn-ghost btn-sm" to="/approvals">{t('dash.approvals.all')}</Link>}
      </div>
      {notice && <div className="panel-b"><div className="alert alert-danger" role="alert">{notice}</div></div>}
      {!allowed ? (
        <Forbidden />
      ) : query.isLoading ? (
        <Skeleton />
      ) : query.error ? (
        <QueryErrorState error={query.error} title={t('dash.approvals.error')} onRetry={() => void query.refetch()} />
      ) : waiting.length === 0 ? (
        <div className="panel-b secondary">{t('dash.approvals.empty')}</div>
      ) : (
        waiting.map((request) => (
          <div key={request.id} className="list-item" style={{ gap: 14, flexWrap: 'wrap' }}>
            <span className="badge b-info">
              <Icon name="clock" />
              {t('dash.approvals.waits', { time: waitedFor(request.requestedAt, t) })}
            </span>
            <div className="stack" style={{ gap: 0, flex: '1 1 320px', minWidth: 0 }}>
              <span>
                <ApprovalSubject request={request} />
              </span>
              <span className="t-sm secondary">
                {t('dash.approvals.step', { step: request.currentStep, total: request.steps.length })}
              </span>
            </div>
            <span className="t-sm muted" style={{ minWidth: 150 }}>{request.requestedByName ?? '—'}</span>
            <span className="row" style={{ gap: 6 }}>
              <button
                className="btn btn-danger-quiet btn-sm"
                type="button"
                disabled={busy}
                onClick={() => {
                  // Отказ без причины сервер не примет: тому, кто просил, неизвестно, что
                  // менять перед повторной попыткой.
                  const comment = window.prompt(t('approvals.needComment'))
                  if (comment?.trim()) reject.mutate({ id: request.id, comment: comment.trim() })
                }}
              >
                {t('dash.approvals.reject')}
              </button>
              <button className="btn btn-secondary btn-sm" type="button" disabled={busy} onClick={() => approve.mutate(request.id)}>
                <Icon name="check" size="sm" />
                {t('approvals.approve')}
              </button>
            </span>
          </div>
        ))
      )}
    </section>
  )
}

const APPROVAL_PATH: Record<string, string> = { quote: '/quotes', invoice: '/invoices', leave: '/leaves' }

function ApprovalSubject({ request }: { request: ApprovalRequest }) {
  const { t } = useApp()
  const label = t(`approvals.entity.${request.entityType}`)
  const base = APPROVAL_PATH[request.entityType]
  return base ? (
    <Link className="strong" to={`${base}/${request.entityId}`}>{label}</Link>
  ) : (
    <span className="strong">{label}</span>
  )
}

// ------------------------------------------------------------------ план/факт

/**
 * План/факт — «не настроено», и это сказано словами (пример из макета).
 *
 * Сводного плана компании в системе нет: цели KPI (#24) ставятся сотрудникам и отделам.
 * Шкала с выдуманным знаменателем выглядела бы рабочей, и по ней принимали бы решения.
 */
function PlanFactBlock() {
  const { t, user } = useApp()
  return (
    <section className="panel" aria-labelledby="dash-h-pf">
      <div className="panel-h">
        <span className="h2" id="dash-h-pf">{t('dash.plan')}</span>
        <span className="badge b-info">{t('state.kind.setup')}</span>
      </div>
      <BlockState
        kind="setup"
        compact
        title={t('dash.plan.setupTitle')}
        description={t('dash.plan.setupText')}
        actions={
          can(user, 'hr.kpi', 'read') ? (
            <Link className="btn btn-secondary btn-sm" to="/kpi">{t('dash.plan.toKpi')}</Link>
          ) : undefined
        }
      />
    </section>
  )
}

// ------------------------------------------------------------------ воронка

function FunnelBlock({
  allowed,
  isLoading,
  error,
  onRetry,
  noFunnel,
  columns,
  conversion,
}: {
  allowed: boolean
  isLoading: boolean
  error: unknown
  onRetry: () => void
  noFunnel: boolean
  columns: BoardColumn[] | undefined
  conversion: StageConversion[] | undefined
}) {
  const { t, locale } = useApp()
  const open = (columns ?? []).filter((column) => column.stageType === 'open')
  const count = open.reduce((sum, column) => sum + column.count, 0)
  const totals = sumTotals(open.flatMap((column) => column.totals))
  // Длина полосы — по сумме, если валюта одна, иначе по числу сделок: рубли с евро на
  // одной шкале сравнивать нельзя.
  const singleCurrency = totals.length <= 1
  const measure = (column: BoardColumn) => (singleCurrency ? Number(column.amount ?? 0) : column.count)
  const max = Math.max(1, ...open.map(measure))

  return (
    <section className="panel" aria-labelledby="dash-h-fn">
      <div className="panel-h">
        <span className="h2" id="dash-h-fn">{t('dash.funnel')}</span>
        <span className="spacer" />
        {columns && (
          <span className="t-sm muted">
            {count} · {formatTotals(totals, locale)}
          </span>
        )}
      </div>
      {!allowed ? (
        <Forbidden />
      ) : isLoading ? (
        <Skeleton />
      ) : error ? (
        <QueryErrorState error={error} title={t('dash.funnel.error')} onRetry={onRetry} />
      ) : noFunnel ? (
        <BlockState kind="setup" compact title={t('dash.funnel.noFunnel')} description={t('dash.funnel.noFunnelText')} />
      ) : count === 0 ? (
        <BlockState kind="empty" compact title={t('dash.funnel.empty')} description={t('dash.funnel.emptyText')} />
      ) : (
        <div className="panel-b stack" style={{ gap: 12 }}>
          {open.map((column, index) => {
            const stage = conversion?.find((line) => line.stageId === column.stageId)
            return (
              <div key={column.stageId} className="md-funnel-row" data-testid={`funnel-${column.stageCode}`}>
                <span className="t-sm">
                  <StagePips order={index + 1} total={open.length} name={column.stageName} />
                </span>
                <div className="stack" style={{ gap: 3 }}>
                  <span className="hbar" style={{ width: `${Math.max(2, (measure(column) / max) * 100)}%` }} />
                  <span className="t-xs muted">
                    {stage?.conversionPercent != null
                      ? t('dash.funnel.conversion', { percent: Math.round(stage.conversionPercent) })
                      : t('dash.funnel.noConversion')}
                  </span>
                </div>
                <span className="num">
                  <span className="strong">{formatTotals(column.totals, locale)}</span>{' '}
                  <span className="muted t-sm">· {column.count}</span>
                </span>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ------------------------------------------------------------------ дебиторка

const BUCKETS = ['notYetDue', 'bucket0to30', 'bucket31to60', 'bucket61to90', 'bucket90plus'] as const
type Bucket = (typeof BUCKETS)[number]

function ReceivablesBlock({
  allowed,
  isLoading,
  error,
  onRetry,
  lines,
}: {
  allowed: boolean
  isLoading: boolean
  error: unknown
  onRetry: () => void
  lines: AgeingLine[] | undefined
}) {
  const { t, locale } = useApp()
  // По валютам: сложить рубли с евро значит выдумать курс. Шкала рисуется для валюты с
  // наибольшим долгом, остальные называются строкой под ней.
  const byCurrency = new Map<string, Record<Bucket, number>>()
  for (const line of lines ?? []) {
    const currency = line.currency ?? '—'
    const sums = byCurrency.get(currency) ?? { notYetDue: 0, bucket0to30: 0, bucket31to60: 0, bucket61to90: 0, bucket90plus: 0 }
    for (const bucket of BUCKETS) sums[bucket] += Number(line[bucket] ?? 0)
    byCurrency.set(currency, sums)
  }
  const ranked = [...byCurrency.entries()]
    .map(([currency, sums]) => ({ currency, sums, total: BUCKETS.reduce((s, b) => s + sums[b], 0) }))
    .sort((a, b) => b.total - a.total)
  const main = ranked[0]
  const others = ranked.slice(1)
  const top = [...(lines ?? [])]
    .filter((line) => Number(line.overdueTotal ?? 0) > 0)
    .sort((a, b) => Number(b.overdueTotal ?? 0) - Number(a.overdueTotal ?? 0))
    .slice(0, 5)

  return (
    <section className="panel" aria-labelledby="dash-h-ar">
      <div className="panel-h">
        <span className="h2" id="dash-h-ar">{t('dash.ar')}</span>
        <span className="spacer" />
        {main && <span className="t-sm muted">{t('dash.ar.total', { amount: money(main.total, main.currency, locale) })}</span>}
      </div>
      {!allowed ? (
        <Forbidden />
      ) : isLoading ? (
        <Skeleton />
      ) : error ? (
        <QueryErrorState error={error} title={t('dash.ar.error')} onRetry={onRetry} />
      ) : !main || main.total === 0 ? (
        <BlockState kind="empty" compact title={t('dash.ar.empty')} description={t('dash.ar.emptyText')} />
      ) : (
        <>
          <div className="panel-b stack" style={{ gap: 10 }}>
            <div
              className="stackbar"
              role="img"
              aria-label={BUCKETS.map((b) => `${t(`dash.ar.${b}`)}: ${money(main.sums[b], main.currency, locale)}`).join('; ')}
            >
              {BUCKETS.map((bucket, index) =>
                main.sums[bucket] > 0 ? (
                  <i key={bucket} style={{ flex: main.sums[bucket], background: `var(--seq-${index})` }} />
                ) : null,
              )}
            </div>
            <div className="md-ar-legend">
              {BUCKETS.map((bucket, index) => (
                <div key={bucket} className="stack" style={{ gap: 0 }} data-testid={`ar-${bucket}`}>
                  <span className="row t-xs muted" style={{ gap: 5 }}>
                    <span className="legend-sw" style={{ background: `var(--seq-${index})` }} />
                    {t(`dash.ar.${bucket}`)}
                  </span>
                  <span className={bucket === 'bucket90plus' && main.sums[bucket] > 0 ? 'strong flag-danger' : 'strong'}>
                    {money(main.sums[bucket], main.currency, locale)}
                  </span>
                  <span className="t-xs muted">{Math.round((main.sums[bucket] / main.total) * 100)} %</span>
                </div>
              ))}
            </div>
            {others.length > 0 && (
              <span className="t-sm secondary">
                {t('dash.ar.otherCurrencies', {
                  amounts: others.map((o) => money(o.total, o.currency, locale)).join(' + '),
                })}
              </span>
            )}
          </div>
          {top.length > 0 && (
            <table className="tbl" aria-label={t('dash.ar.top')}>
              <thead>
                <tr>
                  <th>{t('dash.ar.top')}</th>
                  <th className="num">{t('dash.ar.overdue')}</th>
                  <th>{t('dash.ar.oldest')}</th>
                </tr>
              </thead>
              <tbody>
                {top.map((line) => (
                  <tr key={`${line.counterpartyId}-${line.currency}`}>
                    <td>
                      <Link to={`/counterparties/${line.counterpartyId}`}>{line.counterpartyName ?? '—'}</Link>
                    </td>
                    <td className="num strong">{money(line.overdueTotal ?? 0, line.currency ?? '', locale)}</td>
                    <td>
                      <span className={Number(line.bucket90plus ?? 0) > 0 ? 'flag flag-danger' : 'flag flag-warning'}>
                        {t(`dash.ar.${oldestBucket(line)}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  )
}

function oldestBucket(line: AgeingLine): Bucket {
  for (const bucket of ['bucket90plus', 'bucket61to90', 'bucket31to60', 'bucket0to30'] as const) {
    if (Number(line[bucket] ?? 0) > 0) return bucket
  }
  return 'notYetDue'
}

// ------------------------------------------------------------------ застрявшие

function StuckBlock({
  allowed,
  isLoading,
  error,
  onRetry,
  columns,
}: {
  allowed: boolean
  isLoading: boolean
  error: unknown
  onRetry: () => void
  columns: BoardColumn[] | undefined
}) {
  const { t, locale } = useApp()
  const stageName = new Map((columns ?? []).map((column) => [column.stageId, column.stageName]))
  const stuck: BoardCard[] = (columns ?? [])
    .flatMap((column) => column.cards)
    .filter((card) => card.stuck)
    .sort((a, b) => overstay(b) - overstay(a))
  const shown = stuck.slice(0, 5)
  // Карточки доски — первые 200 на стадию по свежести правки. Если в колонке остались
  // недогруженные, застрявших может быть больше, и это говорится, а не подразумевается.
  const partial = (columns ?? []).some((column) => column.remaining > 0)
  const totals = sumTotals(stuck.map((card) => ({ currency: card.currency, amount: Number(card.amount) })))

  return (
    <section className="panel" aria-labelledby="dash-h-st">
      <div className="panel-h">
        <span className="h2" id="dash-h-st">{t('dash.stuck')}</span>
        {columns && stuck.length > 0 && (
          <span className="count-pill">{stuck.length}</span>
        )}
        {columns && stuck.length > 0 && <span className="t-sm muted">{formatTotals(totals, locale)}</span>}
        <span className="spacer" />
        {allowed && <Link className="btn btn-ghost btn-sm" to="/deals">{t('dash.stuck.board')}</Link>}
      </div>
      {!allowed ? (
        <Forbidden />
      ) : isLoading ? (
        <Skeleton />
      ) : error ? (
        <QueryErrorState error={error} title={t('dash.stuck.error')} onRetry={onRetry} />
      ) : shown.length === 0 ? (
        <div className="panel-b secondary">{t(partial ? 'dash.stuck.emptyPartial' : 'dash.stuck.empty')}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl" aria-label={t('dash.stuck')}>
              <thead>
                <tr>
                  <th>{t('myday.deals.deal')}</th>
                  <th className="num">{t('myday.deals.amount')}</th>
                  <th>{t('dash.stuck.inStage')}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((card) => (
                  <tr key={card.id}>
                    <td className="wrap">
                      <span className="stack" style={{ gap: 0 }}>
                        <Link to={`/deals/${card.id}`}>{card.title}</Link>
                        <span className="t-xs muted">
                          {[card.counterpartyName, stageName.get(card.stageId)].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </td>
                    <td className="num strong">{formatMoney(card.amount, card.currency, locale)}</td>
                    <td>
                      <span className="flag flag-warning">
                        <Icon name="alert" />
                        {card.maxDaysInStage != null
                          ? t('myday.deals.stuck', { days: card.daysInStage, limit: card.maxDaysInStage })
                          : t('myday.deals.stuckNoLimit', { days: card.daysInStage })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {partial && <div className="panel-f secondary">{t('dash.stuck.partial')}</div>}
        </>
      )}
    </section>
  )
}

function overstay(card: BoardCard): number {
  return card.daysInStage - (card.maxDaysInStage ?? 0)
}

// ------------------------------------------------------------------ общее

function Forbidden() {
  const { t } = useApp()
  return <BlockState kind="forbidden" compact title={t('state.forbidden.title')} description={t('state.forbidden.text')} />
}

function Skeleton() {
  return (
    <div className="panel-b stack" style={{ gap: 12 }} aria-busy="true">
      <span className="skel" style={{ width: 140, height: 14 }} />
      <span className="skel" style={{ width: '90%' }} />
      <span className="skel" style={{ width: '70%' }} />
    </div>
  )
}

function sumTotals(items: { currency: string; amount: number | string }[]): { currency: string; amount: number }[] {
  const sums = new Map<string, number>()
  for (const item of items) sums.set(item.currency, (sums.get(item.currency) ?? 0) + Number(item.amount))
  return [...sums.entries()].map(([currency, amount]) => ({ currency, amount }))
}

function money(value: number | string, currency: string, locale: Locale): string {
  // '—' marks a line without a currency: the sum is shown bare rather than as roubles.
  return formatMoney(value, currency && currency !== '—' ? currency : null, locale)
}

/** Текущий месяц до сегодня — период конверсии. */
function monthToDate(today: Date): { from: string; to: string } {
  return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) }
}

function waitedFor(iso: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const hours = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 3_600_000))
  return hours < 24 ? t('dash.hours', { count: hours }) : t('dash.days', { count: Math.floor(hours / 24) })
}
